import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ChevronRight, Loader2, PackageOpen, Truck, CreditCard, MapPin, Printer, CheckCircle2, Clock, PackageCheck, AlertCircle, FileText, Download } from "lucide-react";
import { AccountLayout } from "@/components/account/AccountLayout";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API_BASE_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/account/orders")({
  head: () => ({
    meta: [
      { title: "My Orders & Tracking — Royal Lumina" },
      { name: "description", content: "Track live shipments and view purchase history." },
    ],
  }),
  component: OrdersPage,
});

const statusBadgeStyles: Record<string, { bg: string; text: string; border: string; label: string }> = {
  pending: { bg: "bg-amber-500/10", text: "text-amber-700", border: "border-amber-500/30", label: "Pending" },
  processing: { bg: "bg-gold/10", text: "text-gold", border: "border-gold/40", label: "Processing" },
  shipped: { bg: "bg-blue-500/10", text: "text-blue-700", border: "border-blue-500/30", label: "Shipped" },
  delivered: { bg: "bg-emerald-500/10", text: "text-emerald-700", border: "border-emerald-500/30", label: "Delivered" },
  cancelled: { bg: "bg-destructive/10", text: "text-destructive", border: "border-destructive/30", label: "Cancelled" },
};

function OrdersPage() {
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"all" | "active" | "delivered" | "cancelled">("all");
  const [invoiceOrder, setInvoiceOrder] = useState<any | null>(null);

  // Use auth context — don't rely on stale localStorage token
  const { user, loading: authLoading } = useAuth();
  const token = typeof window !== "undefined" ? localStorage.getItem("supabase.auth.token") || "" : "";

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ["my-orders", user?.id],
    // Only fire when user is confirmed authenticated via Supabase session (not stale localStorage)
    enabled: !authLoading && !!user && !!token,
    queryFn: async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/orders/my-orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) return await res.json();
        console.error("[OrdersPage] Orders fetch failed with status:", res.status, res.statusText);
      } catch (err: any) {
        console.error("[OrdersPage] Orders fetch error:", err?.message ?? err);
      }
      return [];
    }
  });

  if (isLoading) {
    return (
      <AccountLayout title="Orders & Shipments" subtitle="Loading your purchase history...">
        <div className="h-64 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-gold" />
        </div>
      </AccountLayout>
    );
  }

  // Filter orders based on active tab
  const filteredOrders = orders.filter((o: any) => {
    if (activeTab === "active") return ["pending", "processing", "shipped"].includes(o.status);
    if (activeTab === "delivered") return o.status === "delivered";
    if (activeTab === "cancelled") return o.status === "cancelled";
    return true;
  });

  return (
    <AccountLayout title="Orders & Shipments" subtitle={`${orders.length} orders placed at the Royal Lumina atelier.`}>
      
      {/* Filter Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-2 mb-6">
        {[
          { id: "all", label: `All Orders (${orders.length})` },
          { id: "active", label: `Active (${orders.filter((o: any) => ["pending", "processing", "shipped"].includes(o.status)).length})` },
          { id: "delivered", label: `Delivered (${orders.filter((o: any) => o.status === "delivered").length})` },
          { id: "cancelled", label: `Cancelled (${orders.filter((o: any) => o.status === "cancelled").length})` },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`shrink-0 px-4 py-2 rounded-full text-xs font-semibold tracking-wide transition-all ${
              activeTab === tab.id
                ? "bg-primary text-gold shadow-md border border-gold/30"
                : "bg-card border border-border/80 text-muted-foreground hover:text-primary"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {filteredOrders.length === 0 ? (
        <div className="bg-card border border-border/80 rounded-[24px] p-10 text-center shadow-luxe space-y-4">
          <PackageOpen className="h-12 w-12 text-muted-foreground/60 mx-auto" />
          <div>
            <h3 className="font-display text-2xl font-bold text-primary">No Orders Found</h3>
            <p className="text-xs text-muted-foreground mt-1.5 max-w-xs mx-auto">
              {activeTab === "all"
                ? "You haven't placed any candle orders yet. Your rituals await in our boutique."
                : `No orders in the '${activeTab}' category.`}
            </p>
          </div>
          <div className="pt-2">
            <Link to="/shop" className="btn-gold inline-flex h-10 px-6 text-xs uppercase tracking-wider font-semibold">
              Explore Collection
            </Link>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          {filteredOrders.map((o: any) => {
            const firstItem = o.items?.[0];
            const itemImg = firstItem?.product?.img || "/placeholder.svg";
            const itemCount = o.items?.reduce((sum: number, i: any) => sum + i.qty, 0) || 0;
            const dateStr = new Date(o.created_at).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "short",
              year: "numeric"
            });

            const isExpanded = expandedOrderId === o.id;
            const badge = statusBadgeStyles[o.status] || { bg: "bg-muted", text: "text-foreground", border: "border-border", label: o.status };

            return (
              <div
                key={o.id}
                className="bg-card border border-border/80 rounded-[20px] p-5 sm:p-6 shadow-luxe transition-all hover:border-gold/50"
              >
                {/* Top Bar: Order Code, Date & Status */}
                <div 
                  onClick={() => setExpandedOrderId(isExpanded ? null : o.id)}
                  className="flex items-center justify-between border-b border-border/60 pb-4 cursor-pointer select-none"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-gold/10 grid place-items-center text-gold">
                      <FileText className="h-4.5 w-4.5" />
                    </div>
                    <div>
                      <span className="font-sans font-bold text-base sm:text-lg text-primary tracking-wide block">
                        ORDER #{o.id.substring(0, 8).toUpperCase()}
                      </span>
                      <span className="text-[0.7rem] text-muted-foreground font-mono">{dateStr}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className={`text-[0.62rem] font-semibold tracking-widest uppercase px-3 py-1 rounded-full border ${badge.bg} ${badge.text} ${badge.border}`}>
                      {badge.label}
                    </span>
                  </div>
                </div>

                {/* Main Content Row */}
                <div 
                  onClick={() => setExpandedOrderId(isExpanded ? null : o.id)}
                  className="py-4 flex items-center gap-4 sm:gap-6 cursor-pointer select-none"
                >
                  <img
                    src={itemImg}
                    alt=""
                    className="h-16 w-16 sm:h-20 sm:w-20 rounded-[14px] object-cover bg-secondary border border-border/80 shrink-0"
                    onError={(e) => { (e.target as HTMLImageElement).src = "/placeholder.svg"; }}
                  />

                  <div className="min-w-0 flex-1">
                    <h4 className="font-display font-semibold text-base sm:text-lg text-primary truncate">
                      {firstItem?.product?.name || "Handcrafted Scented Candle"}
                    </h4>
                    
                    {firstItem?.variant && (
                      <div className="mt-1 text-[0.68rem] text-gold font-medium bg-gold/10 border border-gold/20 px-2.5 py-0.5 rounded-full inline-block truncate max-w-[220px]">
                        {firstItem.variant}
                      </div>
                    )}

                    <div className="mt-1.5 text-xs text-muted-foreground">
                      {itemCount} {itemCount > 1 ? "items" : "item"} total
                    </div>
                  </div>

                  <div className="text-right shrink-0">
                    <div className="font-display text-xl sm:text-2xl font-bold text-primary">
                      ₹{o.totalAmount?.toLocaleString("en-IN")}
                    </div>
                    <span className={`text-[0.6rem] uppercase tracking-wider font-semibold px-2 py-0.5 rounded mt-1 inline-block ${
                      o.paymentStatus === "paid" ? "bg-emerald-500/10 text-emerald-600" : "bg-amber-500/10 text-amber-600"
                    }`}>
                      {o.paymentStatus === "paid" ? "Paid" : "Unpaid"}
                    </span>
                  </div>
                </div>

                {/* Card Action Row */}
                <div className="flex items-center justify-between pt-3 border-t border-border/60 text-xs font-semibold">
                  <button
                    onClick={() => setExpandedOrderId(isExpanded ? null : o.id)}
                    className="text-gold flex items-center gap-1.5 hover:underline"
                  >
                    <span>{isExpanded ? "Hide tracking & breakdown" : "View tracking & details"}</span>
                    <ChevronRight className={`h-4 w-4 transition-transform duration-300 ${isExpanded ? "rotate-90" : ""}`} />
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setInvoiceOrder(o);
                    }}
                    className="text-muted-foreground hover:text-primary flex items-center gap-1 bg-muted/60 hover:bg-muted px-3 py-1.5 rounded-full text-xs transition-colors"
                  >
                    <Printer className="h-3.5 w-3.5" /> <span>Invoice Receipt</span>
                  </button>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <OrderDetailsSection order={o} token={token} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Invoice Modal */}
      {invoiceOrder && (
        <Dialog open={!!invoiceOrder} onOpenChange={() => setInvoiceOrder(null)}>
          <DialogContent className="max-w-xl bg-card border border-border p-6 sm:p-8 rounded-[24px]">
            <DialogHeader className="border-b border-border pb-4">
              <DialogTitle className="font-display text-2xl font-bold text-primary flex items-center justify-between">
                <span>Tax Invoice Receipt</span>
                <span className="text-xs font-mono font-normal text-gold uppercase px-2.5 py-1 rounded bg-gold/10">
                  ORDER #{invoiceOrder.id.substring(0, 8).toUpperCase()}
                </span>
              </DialogTitle>
            </DialogHeader>

            <div className="py-4 space-y-6 text-sm">
              {/* Company & Customer Details */}
              <div className="flex justify-between items-start text-xs leading-relaxed">
                <div>
                  <h4 className="font-display font-bold text-base text-primary">Royal Lumina Atelier</h4>
                  <p className="text-muted-foreground">Luxury Handcrafted Scented Candles</p>
                  <p className="text-muted-foreground">GSTIN: 27AABCR1234F1ZP</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-primary">{invoiceOrder.shippingAddress?.name}</p>
                  <p className="text-muted-foreground">{invoiceOrder.shippingAddress?.city}, {invoiceOrder.shippingAddress?.state}</p>
                  <p className="text-muted-foreground">Date: {new Date(invoiceOrder.created_at).toLocaleDateString("en-IN")}</p>
                </div>
              </div>

              {/* Items Table */}
              <div className="border border-border/80 rounded-[14px] overflow-hidden">
                <table className="w-full text-left text-xs">
                  <thead className="bg-muted text-muted-foreground uppercase text-[0.62rem] tracking-wider border-b border-border">
                    <tr>
                      <th className="p-3">Item</th>
                      <th className="p-3 text-center">Qty</th>
                      <th className="p-3 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/60 text-primary">
                    {invoiceOrder.items?.map((item: any) => (
                      <tr key={item.id}>
                        <td className="p-3 font-medium">
                          {item.product?.name || "Candle"}
                          {item.variant && <span className="text-[0.68rem] text-muted-foreground block">{item.variant}</span>}
                        </td>
                        <td className="p-3 text-center">{item.qty}</td>
                        <td className="p-3 text-right font-mono">₹{(item.price * item.qty).toLocaleString("en-IN")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Summary */}
              <div className="space-y-1.5 text-xs text-right border-t border-border/60 pt-3">
                <div className="flex justify-between text-muted-foreground">
                  <span>Subtotal</span>
                  <span className="font-mono">₹{invoiceOrder.totalAmount?.toLocaleString("en-IN")}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>GST (Included)</span>
                  <span className="font-mono">₹{Math.round(invoiceOrder.totalAmount * 0.05).toLocaleString("en-IN")}</span>
                </div>
                <div className="flex justify-between font-display text-base font-bold text-primary pt-2 border-t border-border/60">
                  <span>Total Paid</span>
                  <span className="font-mono text-gold">₹{invoiceOrder.totalAmount?.toLocaleString("en-IN")}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-border pt-4">
              <button
                onClick={() => window.print()}
                className="btn-gold h-10 px-5 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2"
              >
                <Printer className="h-4 w-4" /> Print Receipt
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}

    </AccountLayout>
  );
}

function OrderDetailsSection({ order, token }: { order: any; token: string }) {
  const { data: trackingInfo, isLoading } = useQuery({
    queryKey: ["order-tracking", order.id],
    queryFn: async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/orders/${order.id}/track`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.available) return data.tracking;
        }
      } catch (err) {
        console.warn("Shiprocket tracking offline fallback", err);
      }
      return null;
    },
    enabled: !!order.id
  });

  const statuses = ["pending", "processing", "shipped", "delivered"];
  const currentIdx = order.status === "cancelled" ? -1 : statuses.indexOf(order.status);

  return (
    <div className="pt-6 border-t border-border/80 space-y-8 animate-in fade-in duration-300 text-left">
      
      {/* Visual Step Progress Tracker */}
      {order.status !== "cancelled" && (
        <div className="bg-background border border-border/80 rounded-[18px] p-5 space-y-4">
          <div className="text-[0.65rem] font-semibold tracking-[0.25em] uppercase text-gold">Shipment Timeline</div>
          
          <div className="relative flex items-center justify-between px-2 sm:px-6">
            {/* Connecting line */}
            <div className="absolute left-6 right-6 top-4 h-1 bg-border/80 -z-0">
              <div
                className="h-full bg-gold transition-all duration-500"
                style={{ width: `${Math.max(0, (currentIdx / 3) * 100)}%` }}
              />
            </div>

            {/* Nodes */}
            {[
              { id: "pending", label: "Order Placed", desc: "Confirmed" },
              { id: "processing", label: "Handcrafted", desc: "Soy Poured" },
              { id: "shipped", label: "In Transit", desc: "Courier Picked" },
              { id: "delivered", label: "Delivered", desc: "Arrived" },
            ].map((step, idx) => {
              const completed = currentIdx >= idx;
              const isCurrent = currentIdx === idx;

              return (
                <div key={step.id} className="relative z-10 flex flex-col items-center text-center">
                  <div
                    className={`h-9 w-9 rounded-full grid place-items-center transition-all ${
                      completed
                        ? "bg-gold text-primary font-bold shadow-md shadow-gold/20"
                        : "bg-background border-2 border-border text-muted-foreground"
                    } ${isCurrent ? "ring-4 ring-gold/20" : ""}`}
                  >
                    {completed ? <CheckCircle2 className="h-5 w-5" /> : <span className="text-xs">{idx + 1}</span>}
                  </div>
                  <span className={`text-xs font-semibold mt-2 ${completed ? "text-primary" : "text-muted-foreground"}`}>
                    {step.label}
                  </span>
                  <span className="text-[0.65rem] text-muted-foreground hidden sm:block">{step.desc}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Items Breakdown */}
      <div className="space-y-3">
        <div className="text-[0.65rem] font-semibold tracking-[0.25em] uppercase text-muted-foreground">Order Items Breakdown</div>
        <div className="bg-background border border-border/80 rounded-[18px] divide-y divide-border/60 overflow-hidden">
          {order.items?.map((item: any) => (
            <div key={item.id} className="p-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="h-7 w-7 rounded-full bg-gold/10 text-gold font-bold text-xs grid place-items-center">
                  {item.qty}x
                </span>
                <div>
                  <span className="font-display font-semibold text-sm text-primary block">
                    {item.product?.name || "Premium Candle"}
                  </span>
                  {item.variant && <span className="text-xs text-muted-foreground">{item.variant}</span>}
                </div>
              </div>
              <span className="font-mono font-medium text-sm text-primary">₹{(item.price * item.qty).toLocaleString("en-IN")}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Shipping Address & Payment Method */}
      <div className="grid md:grid-cols-2 gap-5">
        <div className="bg-background border border-border/80 rounded-[18px] p-5 space-y-2">
          <div className="text-[0.65rem] font-semibold tracking-[0.25em] uppercase text-gold flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" /> Delivery Address
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed pt-1">
            <span className="font-bold text-primary block text-sm">{order.shippingAddress?.name}</span>
            {order.shippingAddress?.street}, {order.shippingAddress?.city}, {order.shippingAddress?.state} - {order.shippingAddress?.zip}<br />
            {order.shippingAddress?.phone && <span className="font-mono text-primary/80 block mt-1">{order.shippingAddress?.phone}</span>}
          </p>
        </div>

        <div className="bg-background border border-border/80 rounded-[18px] p-5 space-y-2">
          <div className="text-[0.65rem] font-semibold tracking-[0.25em] uppercase text-gold flex items-center gap-1.5">
            <CreditCard className="h-3.5 w-3.5" /> Payment Information
          </div>
          <div className="pt-1 text-xs text-primary font-medium space-y-1">
            <p className="capitalize font-semibold text-sm">
              {order.shippingAddress?.paymentMethod === 'cod' ? 'Cash on Delivery (COD)' : 
               order.shippingAddress?.paymentMethod === 'upi' ? 'UPI / QR Code' : 
               order.shippingAddress?.paymentMethod === 'card' ? 'Debit/Credit Card' : 'Cash on Delivery (COD)'}
            </p>
            <span className={`px-2.5 py-0.5 rounded text-[0.6rem] font-semibold uppercase tracking-wider inline-block ${
              order.paymentStatus === 'paid' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-amber-500/10 text-amber-600'
            }`}>
              {order.paymentStatus === 'paid' ? 'Payment Verified' : 'Payment Pending'}
            </span>
          </div>
        </div>
      </div>

    </div>
  );
}
